import React, { useState, useEffect, useCallback } from "react";
import { ListGroup, ListGroupItem } from "../ListGroup";
import UnclaimedTaskCard from "./UnclaimedTaskCard";
import { useTaskList } from "../../hooks/useTaskList";
import api from "lib/api";
import { useTranslation } from "react-i18next";
import usePagedData from "hooks/usePagedData";
import { Loading } from "@alm/flow-ds/components";
import TasksHeading from "./TasksHeading";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { getCheckedBoxes } from "data/app/appSelectors";
import {
	addCheckedBox,
	removeCheckedBox,
	setCheckedBoxes,
} from "data/app/appActions";
import { useSelector, useDispatch } from "react-redux";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";

interface Props {
	search: string;
}

function UnclaimedTasks({ search }: Props) {
	const { t } = useTranslation();

	const [init, setInit] = useState(false);

	const checkboxes = useSelector(getCheckedBoxes);
	const reduxDispatch = useDispatch();

	// Hook for paging the unassigned tasks
	const { state, setData, ready, patchState, loading, searching } =
		usePagedData();

	// Hook for state changes in the unassigned task
	const { changePriority, claimTask } = useTaskList(state, patchState);

	const [currentPage, setCurrentPage] = useState(1);

	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	const { limit } = state;

	const disableOnLoad = state.loading ? "disabled" : "";

	/**
	 * Getting the unclaimed tasks when component first mounted
	 */
	useEffect(() => {
		if (!init) {
			// Load activities from API
			api.workflow
				.getUnclaimedTasks({ offset: 0, limit: 10 })
				.then(results => {
					setData(results);
					setTotal(results.totalCount);
					ready();
					setInit(true);
				})
				.catch(e => console.error(e));
		}
	}, [init, ready, setData]);

	/**
	 * Searching the unassigned tasks with the search term
	 */
	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			// Load activities from API with search query
			api.workflow
				.getUnclaimedTasks({ limit: 10, offset: 0, search })
				.then(results => {
					setData(results);
					setTotal(results.totalCount);
					ready();
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	/**
	 * Paging the unassigned tasks when the page button is clicked
	 */
	const pageData = useCallback(
		async (page: number) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Page unclaimed tasks",
			});

			loading();
			try {
				const res = await api.workflow.getUnclaimedTasks({
					limit: limit,
					offset: (page - 1) * limit,
					search: state.search,
				});
				setData(res);
				ready();
			} catch (e) {
				console.error(e);
			}
			setCurrentPage(page);
		},
		[setData, loading, ready, limit, state.search]
	);

	/**
	 * Dispatch a list of selected checkboxes
	 */
	useEffect(() => {
		const newCheckboxes: string[] = [];
		for (const task of state.results) {
			if (checkboxes.includes(task.id)) {
				newCheckboxes.push(task.id);
			}
		}
		reduxDispatch(setCheckedBoxes(newCheckboxes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.results]);

	const handleSelectAllCheckboxesClick = useCallback(() => {
		const newCheckboxes: string[] = [];
		if (!(checkboxes.length === state.results.length)) {
			for (const task of state.results) {
				newCheckboxes.push(task.id);
			}
		}
		reduxDispatch(setCheckedBoxes(newCheckboxes));
	}, [checkboxes.length, reduxDispatch, state.results]);

	const handleCheckboxClick = useCallback(
		(id: string) => () => {
			checkboxes.includes(id)
				? reduxDispatch(removeCheckedBox(id))
				: reduxDispatch(addCheckedBox(id));
		},
		[checkboxes, reduxDispatch]
	);

	if (!init || state.searching)
		return <Loading className="mt-4 elem__loader--md" />;
	else
		return (
			<>
				{state.results.length ? (
					<>
						<div className="tasks-container--select">
							<div
								className="tasks--selectedAll"
								onClick={handleSelectAllCheckboxesClick}>
								{checkboxes.length === state.results.length
									? t("main.tasks.labels.deselectAll")
									: t("main.tasks.labels.selectAll")}
							</div>
						</div>
						<TasksHeading
							unclaimed
							handleSelectAllCheckboxesClick={
								handleSelectAllCheckboxesClick
							}
							tasksCount={state.results.length}
							claimTask={claimTask}
							loading={state.loading}
						/>
						<ListGroup className="tasks-group">
							{state.results.map((task, index: number) => {
								return (
									<ListGroupItem
										className={`task-card task-card--unclaimed  ${
											checkboxes.includes(task.id)
												? "task-card--selected"
												: ""
										} ${disableOnLoad}`}
										key={`unclaimedTask__${index}`}>
										<UnclaimedTaskCard
											{...task}
											changePriority={changePriority}
											claimTask={claimTask}
											handleCheckboxClick={
												handleCheckboxClick
											}
											isChecked={checkboxes.includes(
												task.id
											)}
											loading={state.loading}
										/>
									</ListGroupItem>
								);
							})}
						</ListGroup>
					</>
				) : (
					!state.loading && (
						<p className="no-tasks">
							{t(
								search.trim() !== ""
									? "main.tasks.text.noSearch"
									: "main.tasks.text.noUnclaimedTasks"
							)}
						</p>
					)
				)}
				{total !== undefined && total > limit && !state.searching && (
					<PaginationBar
						currentPage={currentPage}
						limit={limit}
						total={total}
						paginationLimit={paginationLimit}
						onPageChange={page => {
							pageData(page);
						}}
						loading={state.loading}
					/>
				)}
			</>
		);
}

export default UnclaimedTasks;
